import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, X } from "lucide-react";
import { useRef, useState } from "react";
import type { Id, Warehouse, WriteRejection } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RejectionNotice } from "@/modules/warehouses/screens/RejectionNotice";
import {
  buildCreateTransferInput,
  emptyLine,
  isCreateDraftComplete,
  type CreateLineDraft,
} from "@/modules/warehouses/transferDrafts";
import { useVariantCatalog } from "@/modules/warehouses/variantCatalog";

/**
 * New transfer (M6-14, FR-WHS-5).
 *
 * Source and destination are two independent selects, neither filtered
 * against the other's value. The fixture adapter refuses a transfer whose
 * source and destination match with an `invalid` rejection — the same rule
 * the real API will enforce — so this form lets someone pick the same
 * warehouse twice and submit, and shows the refusal that comes back rather
 * than pretending the constraint lives here.
 */
export function CreateTransferForm({
  warehouses,
  onCreated,
  onCancel,
}: {
  warehouses: Warehouse[];
  onCreated: (transferId: Id) => void;
  onCancel: () => void;
}) {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const catalog = useVariantCatalog(true);

  const [fromWarehouseId, setFromWarehouseId] = useState("");
  const [toWarehouseId, setToWarehouseId] = useState("");
  const [note, setNote] = useState("");
  const [lines, setLines] = useState<CreateLineDraft[]>([emptyLine("l0")]);
  const [rejection, setRejection] = useState<WriteRejection | null>(null);
  // Never reused, even after a line is removed — reusing a freed key is how
  // React ends up recycling a deleted line's input state onto the next one
  // added in the same slot.
  const nextLineKey = useRef(1);

  const draft = { fromWarehouseId, toWarehouseId, note, lines };

  const mutation = useMutation({
    mutationFn: () =>
      data.warehouses.createTransfer(buildCreateTransferInput(draft)),
    onSuccess: (result) => {
      if (result.ok) {
        // Write-through: the new transfer is whatever `createTransfer`
        // returned, never the draft this form held — invalidating rather
        // than seeding the cache from `draft` is what keeps that true.
        void queryClient.invalidateQueries({ queryKey: ["warehouses"] });
        onCreated(result.data.id);
      } else {
        setRejection(result.rejection);
      }
    },
  });

  function updateLine(key: string, patch: Partial<CreateLineDraft>) {
    setLines((current) =>
      current.map((line) => (line.key === key ? { ...line, ...patch } : line)),
    );
  }

  function removeLine(key: string) {
    setLines((current) => current.filter((line) => line.key !== key));
  }

  function addLine() {
    const key = `l${String(nextLineKey.current)}`;
    nextLineKey.current += 1;
    setLines((current) => [...current, emptyLine(key)]);
  }

  return (
    <div className="flex flex-col gap-4 p-1">
      <p className="text-sm font-medium">New transfer</p>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label htmlFor="transfer-from">From</Label>
          <Select value={fromWarehouseId} onValueChange={setFromWarehouseId}>
            <SelectTrigger id="transfer-from" size="sm" className="w-full">
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              {warehouses.map((warehouse) => (
                <SelectItem key={warehouse.id} value={warehouse.id}>
                  {warehouse.name} ({warehouse.code})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label htmlFor="transfer-to">To</Label>
          <Select value={toWarehouseId} onValueChange={setToWarehouseId}>
            <SelectTrigger id="transfer-to" size="sm" className="w-full">
              <SelectValue placeholder="Destination" />
            </SelectTrigger>
            <SelectContent>
              {warehouses.map((warehouse) => (
                <SelectItem key={warehouse.id} value={warehouse.id}>
                  {warehouse.name} ({warehouse.code})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1">
        <Label htmlFor="transfer-note">Note (optional)</Label>
        <Input
          id="transfer-note"
          value={note}
          onChange={(event) => {
            setNote(event.target.value);
          }}
          placeholder="e.g. Festive season top-up"
        />
      </div>

      <div className="space-y-2">
        <Label>Lines</Label>
        {lines.map((line) => (
          <div key={line.key} className="flex items-center gap-2">
            <Select
              value={line.variantId}
              onValueChange={(value) => {
                updateLine(line.key, { variantId: value });
              }}
            >
              <SelectTrigger size="sm" className="w-full flex-1">
                <SelectValue placeholder="Choose a variant" />
              </SelectTrigger>
              <SelectContent>
                {catalog.options.map((option) => (
                  <SelectItem key={option.id} value={option.id}>
                    {option.sku ? `${option.sku} — ` : ""}
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="number"
              min={1}
              step={1}
              value={line.quantity}
              onChange={(event) => {
                updateLine(line.key, { quantity: event.target.value });
              }}
              className="w-20"
              aria-label="Quantity"
            />
            <Button
              type="button"
              size="icon-sm"
              variant="ghost"
              disabled={lines.length === 1}
              onClick={() => {
                removeLine(line.key);
              }}
            >
              <X />
            </Button>
          </div>
        ))}
        <Button type="button" size="sm" variant="outline" onClick={addLine}>
          <Plus />
          Add line
        </Button>
        {catalog.isError ? (
          <p className="text-destructive text-xs">
            {catalog.error?.message ?? "Could not load variants."}
          </p>
        ) : null}
      </div>

      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          disabled={!isCreateDraftComplete(draft) || mutation.isPending}
          onClick={() => {
            mutation.mutate();
          }}
        >
          {mutation.isPending ? <Loader2 className="animate-spin" /> : null}
          Create transfer
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={mutation.isPending}
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>

      {rejection ? (
        <RejectionNotice
          rejection={rejection}
          onReload={() => {
            setRejection(null);
          }}
        />
      ) : null}
    </div>
  );
}
